import { BaseService } from "./BaseService";
import { ITextBlockArray } from "Entities";
import { QueryParameter } from "Core/QueryParameter";
import { Configuration } from "Configuration";
import { Util } from "Core/Util";
import { TextBlockClient } from "./Client/ServiceClient";
import { AppContext } from "Core";

/**
 * Provides access to TextBlocks
 */
export class TextBlockService extends BaseService {
    private textblockPromises: Promise<{ [textBlockCategory: string]: ITextBlockArray }>[] = [];
    private missingTextBlocks: { [textBlockCategory: string]: ITextBlockArray } = {};

    private textBlocks: { [textBlockCategory: string]: ITextBlockArray } = {};

    /**
     * Loads TextBlocks from server.
     * @param languageCode LanguageCode i.e. "E", "D".
     * @param textBlockCategory TextBlockCategory, usually the mappingName in lowercase.
     */
    public async load(
        languageCode: string,
        textBlockCategory: string,
        cached: boolean = true
    ): Promise<{ [textBlockCategory: string]: ITextBlockArray }> {
        if (this.textBlocks[textBlockCategory] && cached) {
            return this.textBlocks;
        }
        let tb = await this.loadInternal(languageCode, textBlockCategory);
        return tb;
    }

    private loadInternal(languageCode: string, textBlockCategory: string): Promise<{ [textBlockCategory: string]: ITextBlockArray }> {
        let client = new TextBlockClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        const promise = new Promise<{ [textBlockCategory: string]: ITextBlockArray }>((resolve, reject) => {
            client.get(
                languageCode,
                textBlockCategory,
                (res) => resolve(res),
                (exception, reason) => {
                    AppContext.handleServiceResult(JSON.parse((<any>exception).response));
                    reject(reason);
                }
            );
            // cancellationTokenSource.token.onCancelled((reason) => {
            //     xhr.abort();
            //     reject(reason);
            // });
        }).then((tb) => {
            this.textBlocks = { ...this.textBlocks, ...tb };
            return tb;
        });
        this.textblockPromises.push(promise);
        return promise;
    }

    /**
     * Gets all last loaded TextBlocks, usually the TextBlocks of the loaded mappings.
     */
    public getAll(): Promise<{ [textBlockCategory: string]: ITextBlockArray }[]> {
        return Promise.all(this.textblockPromises);
    }

    /**
     * Gets a single TextBlock for the current mapping.
     * @param textBlockName Name of the TextBlock.
     * @param textBlockCategory (Optional) Name of the textBlockCategory. If omitted, the currentTextBlockCategory is used.
     * @param returnEmpty (Optional) True if an empty string should be returned if the textblock is missing.
     */
    public get(textBlockName: string, textBlockCategory?: string, returnEmpty?: boolean): string {
        return this.getChecked(this.textBlocks[textBlockCategory], textBlockName, textBlockCategory, returnEmpty);
    }

    /**
     * Gets the TextBlock from the given ITextBlockArray with checks if the TextBlock exists.
     * Resolves the TextBlock if it contains a script.
     * @param textBlocks List of current TextBlocks.
     * @param textBlockName Name of TextBlock.
     * @param returnEmpty (Optional) True if an empty string should be returned if the textblock is missing.
     */
    public getChecked(textBlocks: ITextBlockArray, textBlockName: string, textBlockCategory?: string, returnEmpty?: boolean): string {
        if (!textBlockName) {
            return "";
        }
        if (textBlocks == null) {
            return "";
        }

        if (textBlockName.substr(0, 6) === "\\\\#\\\\#") {
            return textBlockName.substr(6);
        }

        if (!textBlocks.hasOwnProperty(textBlockName)) {
            if (!this.missingTextBlocks[textBlockCategory]) {
                this.missingTextBlocks[textBlockCategory] = {};
            }
            this.missingTextBlocks[textBlockCategory][textBlockName] = "";
            if (returnEmpty) {
                return "";
            } else {
                return `<span class='dms-missing-textblock'>${textBlockName}</span>`;
            }
        }
        let textBlock = textBlocks[textBlockName];
        if (textBlock == null) {
            if (returnEmpty) {
                return "";
            } else {
                return `<span class='dms-missing-textblock'>${textBlockName}</span>`;
            }
        }
        if (textBlock && textBlock.indexOf("#") >= 0) {
            textBlock = Util.resolve(textBlock, {});
        }
        return textBlock;
    }

    public setTextBlocks(languageCode: string): Promise<void> {
        if (this.missingTextBlocks) {
            let autogenerateTextBlockParam = (QueryParameter.get("autogeneratetb") || "").toLowerCase();
            let autogenerateTextBlock = Util.parseBoolean(autogenerateTextBlockParam);

            let client = new TextBlockClient(Configuration.baseUrl);
            client.beforeSend = this.getBeforeSend();
            let textblocks = this.missingTextBlocks;
            this.missingTextBlocks = {};
            return new Promise<void>((resolve, reject) => {
                client.set(
                    languageCode,
                    autogenerateTextBlock,
                    textblocks,
                    () => resolve(),
                    (exception, reason) => {
                        AppContext.handleServiceResult(JSON.parse((<any>exception).response));
                        reject(reason);
                    }
                );
                // cancellationTokenSource.token.onCancelled((reason) => {
                //     xhr.abort();
                //     reject(reason);
                // });
            });
        }
    }
}
